import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isEqual } from 'lodash';
import { catchError, EMPTY, Subscription, tap } from 'rxjs';
import { MlValidationFeedbacksApiService } from '@shared/services/ml-validation-feedbacks-api/ml-validation-feedbacks-api.service';
import {
  MlValidationFeedback,
  MlValidationFeedbackPatch,
  MlValidationFeedbackRow,
  MlValidationFeedbackRowPatch,
  MlValidationFeedbacksStateModel,
  MlValidationFeedbackTableQuery,
  Pagination,
} from '@shared/models';
import { DEFAULT_PAGINATION } from '@shared/utils';

export const defaultMlValidationFeedbacksState: MlValidationFeedbacksStateModel = {
  mlValidationFeedbacks: [],
  total: 0,
  isLoading: false,
  error: undefined,
  success: undefined,
  pagination: DEFAULT_PAGINATION,
};

const beforeApiCall: Partial<MlValidationFeedbacksStateModel> = {
  isLoading: true,
  error: undefined,
  success: undefined,
};

const successStatus: Partial<MlValidationFeedbacksStateModel> = {
  isLoading: false,
  error: undefined,
};

const errorStatus = (error: Error): Partial<MlValidationFeedbacksStateModel> => ({
  error: error.message,
  isLoading: false,
  success: undefined,
});

const SCALAR_KEYS = [
  'customer',
  'eventId',
  'omniId',
  'component',
  'predictionTimestamp',
  'reviewer',
  'reviewStatus',
] as const satisfies readonly (keyof Omit<MlValidationFeedback, 'id' | 'rows' | 'feedbackTimestamp'>)[];

const ROW_REVIEWER_KEYS: readonly (keyof MlValidationFeedbackRow)[] = [
  'reviewerResult',
  'predictionUseful',
  'actionable',
  'observationCategory',
  'feedback',
  'attachments',
];

/** Builds a patch containing only the fields that differ between the original and updated feedback. */
export function computeMlValidationFeedbackDiff(
  original: MlValidationFeedback,
  updated: MlValidationFeedback
): MlValidationFeedbackPatch {
  const diff: MlValidationFeedbackPatch = {
    id: updated.id,
  };

  for (const key of SCALAR_KEYS) {
    if (!isEqual(updated[key], original[key])) {
      (diff as Record<string, unknown>)[key] = updated[key];
    }
  }

  const originalRowsById = new Map(original.rows.map((row) => [row.id, row]));

  const changedRows = updated.rows
    .map((row) => {
      const originalRow = originalRowsById.get(row.id);
      if (!originalRow) {
        return row;
      }

      const rowDiff: MlValidationFeedbackRowPatch = { predictionType: row.predictionType, id: row.id };
      let hasChanges = false;

      for (const key of ROW_REVIEWER_KEYS) {
        if (!isEqual(row[key], originalRow[key])) {
          (rowDiff as Record<string, unknown>)[key] = row[key];
          hasChanges = true;
        }
      }

      return hasChanges ? rowDiff : null;
    })
    .filter((row): row is MlValidationFeedbackRowPatch => row !== null);

  if (changedRows.length > 0) {
    diff.rows = changedRows;
  }

  return diff;
}

/** Signal-based store for ML validation feedbacks. */
@Injectable({ providedIn: 'root' })
export class MlValidationFeedbacksStore {
  private readonly mlValidationFeedbacksApi = inject(MlValidationFeedbacksApiService);
  private readonly state = signal<MlValidationFeedbacksStateModel>(defaultMlValidationFeedbacksState);
  private readonly destroyRef = inject(DestroyRef);
  private fetchSubscription?: Subscription;
  private tableQuery?: MlValidationFeedbackTableQuery;

  readonly mlValidationFeedbacks = computed(() => this.state().mlValidationFeedbacks);
  readonly total = computed(() => this.state().total);
  readonly selected = computed(() => this.state().selected);
  readonly isLoading = computed(() => this.state().isLoading);
  readonly error = computed(() => this.state().error);
  readonly success = computed(() => this.state().success);
  readonly pagination = computed(() => this.state().pagination);

  fetchMlValidationFeedbacks(pagination: Pagination, tableQuery?: MlValidationFeedbackTableQuery): void {
    this.fetchSubscription?.unsubscribe();
    this.tableQuery = tableQuery;
    this.patch({ ...beforeApiCall, pagination });

    this.fetchSubscription = this.mlValidationFeedbacksApi
      .getMlValidationFeedbacks(pagination, tableQuery)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(({ data, total }) => this.patch({ ...successStatus, mlValidationFeedbacks: data, total })),
        catchError((error: Error) => {
          this.patch(errorStatus(error));
          return EMPTY;
        })
      )
      .subscribe();
  }

  patchSelectedFeedback(selected?: MlValidationFeedback): void {
    this.patch({ selected });
  }

  updatePagination(pagination: Pagination): void {
    this.patch({ pagination });
  }

  saveMlValidationFeedback(mlValidationFeedback: MlValidationFeedback): void {
    const { mlValidationFeedbacks } = this.state();
    this.patch({ ...beforeApiCall, mlValidationFeedbacks });

    const updated: MlValidationFeedback = { ...mlValidationFeedback };
    const original = mlValidationFeedbacks.find((feedback) => feedback.id === updated.id);
    const payload = original ? computeMlValidationFeedbackDiff(original, updated) : updated;

    if (payload.rows?.length === 0 && payload.reviewStatus === undefined) {
      return;
    }

    if (payload.reviewStatus != original?.reviewStatus) {
      payload.feedbackTimestamp = payload.reviewStatus === 'Closed' ? new Date() : null;
    }

    this.mlValidationFeedbacksApi
      .saveMlValidationFeedback(payload)
      .pipe(
        tap(() => {
          this.patch({ ...successStatus, success: 'FEEDBACKS.SAVE_SUCCESS' });
          this.fetchMlValidationFeedbacks(this.pagination(), this.tableQuery);
        }),
        catchError((error: Error) => {
          this.patch(errorStatus(error));
          this.fetchMlValidationFeedbacks(this.pagination(), this.tableQuery);
          return EMPTY;
        })
      )
      .subscribe();
  }

  private patch(partial: Partial<MlValidationFeedbacksStateModel>): void {
    this.state.update((current) => ({ ...current, ...partial }));
  }
}
